import React, { useCallback, useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Trash2, Camera, ChevronLeft, ChevronRight, Plus, ShieldAlert, Lock, Calendar, Check } from "lucide-react";

const SCOPES = [
  { id: "full", label: "Full Access", hint: "Workouts + Meals + Progress + Billing" },
  { id: "workouts_only", label: "Workouts Only", hint: "Hide meal-plan tools" },
  { id: "meals_only", label: "Meals Only", hint: "Hide workout tools" },
];

export default function OneOnOneTab() {
  const [clients, setClients] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [noteIdx, setNoteIdx] = useState(0);
  const [savingNote, setSavingNote] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [scopeBusy, setScopeBusy] = useState(false);
  const [terminateOpen, setTerminateOpen] = useState(false);
  const [terminateBusy, setTerminateBusy] = useState(false);
  const [pendingRequests, setPendingRequests] = useState([]);

  const loadRequests = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/session-requests?status=pending");
      setPendingRequests(data);
    } catch (e) {
      // non-fatal
    }
  }, []);

  const handleRequest = async (id) => {
    try {
      await api.post(`/admin/session-requests/${id}/handle`);
      toast.success("Request marked as handled");
      await loadRequests();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const loadList = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/one-on-one-clients");
      setClients(data);
      setSelectedId((curr) => (curr || (data.length > 0 ? data[0].id : null)));
    } catch (e) {
      toast.error("Failed to load 1-on-1 clients");
    }
  }, []);

  const loadDetail = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/one-on-one-clients/${id}`);
      setDetail(data);
      setNoteIdx(0);
    } catch (e) {
      toast.error("Failed to load client detail");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadList(); loadRequests(); }, [loadList, loadRequests]);
  useEffect(() => { if (selectedId) loadDetail(selectedId); }, [selectedId, loadDetail]);

  const addNote = async () => {
    const t = noteText.trim();
    if (!t) { toast.error("Note can't be empty"); return; }
    setSavingNote(true);
    try {
      await api.post(`/admin/one-on-one-clients/${selectedId}/notes`, { text: t });
      setNoteText("");
      toast.success("Note saved");
      await loadDetail(selectedId);
      await loadList();
      setNoteIdx(0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save note");
    } finally {
      setSavingNote(false);
    }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await api.delete(`/admin/one-on-one-clients/${selectedId}/notes/${noteId}`);
      toast.success("Note deleted");
      await loadDetail(selectedId);
      await loadList();
      setNoteIdx(0);
    } catch (e) {
      toast.error("Failed to delete note");
    }
  };

  const redeemSession = async () => {
    setRedeeming(true);
    try {
      const { data } = await api.post(`/admin/one-on-one-clients/${selectedId}/sessions/redeem`);
      toast.success(`Session #${data.session_count} redeemed`);
      await loadDetail(selectedId);
      await loadList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setRedeeming(false);
    }
  };

  const unredeemSession = async (sid) => {
    if (!window.confirm("Remove this session entry?")) return;
    try {
      await api.delete(`/admin/one-on-one-clients/${selectedId}/sessions/${sid}`);
      toast.success("Removed");
      await loadDetail(selectedId);
      await loadList();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const changeScope = async (newScope) => {
    if (!detail || detail.access_scope === newScope) return;
    setScopeBusy(true);
    try {
      await api.post(`/admin/members/${selectedId}/access`, { access_scope: newScope });
      toast.success(`Access updated → ${newScope.replace("_", " ")}`);
      await loadDetail(selectedId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update access");
    } finally {
      setScopeBusy(false);
    }
  };

  const terminateClient = async () => {
    setTerminateBusy(true);
    try {
      await api.delete(`/admin/members/${selectedId}`);
      toast.success(`${detail.email} terminated`);
      setTerminateOpen(false);
      setSelectedId(null);
      setDetail(null);
      await loadList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to terminate");
    } finally {
      setTerminateBusy(false);
    }
  };

  const notes = detail?.notes || [];
  const note = notes[noteIdx] || null;

  return (
    <div data-testid="tab-oneonone">
      {pendingRequests.length > 0 && (
        <div className="mb-6 border border-[#E5B43A] bg-[#E5B43A]/10 p-5" data-testid="pending-session-requests">
          <div className="flex items-center gap-2 mb-3">
            <Calendar size={14} className="text-[#E5B43A]" />
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#E5B43A] font-bold">
              Pending Session Requests · {pendingRequests.length}
            </div>
          </div>
          <div className="space-y-3">
            {pendingRequests.map((r) => (
              <div key={r.id} className="border border-zinc-800 bg-[#0A0A0A] p-4 flex items-start justify-between gap-3 flex-wrap" data-testid={`session-request-${r.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-display text-lg leading-tight">{r.member_name}</div>
                  <div className="text-xs text-zinc-400 break-all">{r.member_email}</div>
                  <div className="mt-2 text-[10px] tracking-widest uppercase text-zinc-500">
                    Mode · <span className="text-[#E5B43A]">{(r.mode || "").replace("_", "-")}</span>
                    <span className="text-zinc-700 mx-2">|</span>
                    Tier · <span className="text-zinc-300">{(r.tier || "—").replace(/_/g, " ")}</span>
                  </div>
                  <div className="mt-2 text-xs text-zinc-300">
                    <b className="text-[#E5B43A] text-[10px] tracking-widest uppercase">Slots:</b>
                    <ul className="list-disc list-inside ml-1">
                      {(r.slot_options || []).map((s, i) => (<li key={`${r.id}-slot-${i}`}>{s}</li>))}
                    </ul>
                  </div>
                  {r.note && <div className="mt-2 text-xs text-zinc-400 italic">"{r.note}"</div>}
                  <div className="mt-2 text-[10px] text-zinc-600">{new Date(r.created_at).toLocaleString()}</div>
                </div>
                <button
                  onClick={() => handleRequest(r.id)}
                  data-testid={`session-request-handle-${r.id}`}
                  className="inline-flex items-center gap-2 bg-[#E5B43A] text-black px-3 py-2 text-[10px] font-bold uppercase tracking-widest hover:bg-white transition-colors shrink-0"
                >
                  <Check size={12} /> Mark Handled
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-3 border border-zinc-800 bg-[#141414]" data-testid="oneonone-sidebar">
          <div className="px-5 py-3 border-b border-zinc-800 text-[10px] tracking-[0.3em] uppercase text-[#E5B43A]">
            Training Clients ({clients.length})
          </div>
          {clients.length === 0 ? (
            <div className="px-5 py-6 text-sm text-zinc-500">No 1-on-1 clients yet. Approve a 1-on-1 application from the Memberships tab.</div>
          ) : (
            <ul className="divide-y divide-zinc-900">
              {clients.map((c) => (
                <li key={c.id}>
                  <button
                    onClick={() => setSelectedId(c.id)}
                    data-testid={`client-${c.id}`}
                    className={`w-full text-left px-5 py-4 hover:bg-[#1a1a1a] transition-colors ${selectedId === c.id ? "bg-[#1a1a1a] border-l-2 border-[#E5B43A]" : ""}`}
                  >
                    <div className="font-bold text-sm truncate">{c.name}</div>
                    <div className="text-xs text-zinc-500 truncate">{c.email}</div>
                    <div className="mt-2 flex gap-2 text-[10px] tracking-widest uppercase">
                      <span className="text-zinc-400">{c.session_count} sess</span>
                      <span className="text-zinc-600">·</span>
                      <span className="text-zinc-400">{c.notes_count} notes</span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <section className="lg:col-span-9">
          {!detail ? (
            <div className="border border-zinc-800 bg-[#141414] p-10 text-center text-zinc-500 text-sm">
              {loading ? "Loading…" : "Select a 1-on-1 client to view their info."}
            </div>
          ) : (
            <div className="space-y-6">
              <div className="border border-zinc-800 bg-[#141414] p-5 md:p-6" data-testid="contact-card">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#E5B43A] mb-2">Contact</div>
                <div className="font-display text-2xl md:text-3xl break-words">{detail.name}</div>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                  <div><span className="text-zinc-500 text-[10px] tracking-widest uppercase mr-2">Email</span><span className="break-all">{detail.email}</span></div>
                  <div><span className="text-zinc-500 text-[10px] tracking-widest uppercase mr-2">Phone</span>{detail.phone || <span className="text-zinc-600 italic">not provided</span>}</div>
                  <div><span className="text-zinc-500 text-[10px] tracking-widest uppercase mr-2">Active Until</span>{detail.subscription_until ? new Date(detail.subscription_until).toLocaleDateString() : "—"}</div>
                  <div><span className="text-zinc-500 text-[10px] tracking-widest uppercase mr-2">Joined</span>{detail.created_at ? new Date(detail.created_at).toLocaleDateString() : "—"}</div>
                  <div><span className="text-zinc-500 text-[10px] tracking-widest uppercase mr-2">Tier</span>{(detail.tier || "—").replace(/_/g, " ")}{typeof detail.sessions_quota === "number" && detail.sessions_quota > 0 ? ` · ${detail.sessions_quota} session${detail.sessions_quota === 1 ? "" : "s"}/mo` : detail.sessions_quota === null ? " · unlimited sessions" : ""}</div>
                  {detail.active_goal && <div><span className="text-zinc-500 text-[10px] tracking-widest uppercase mr-2">Nutrition Goal</span>{detail.active_goal.replace("_", " ")}</div>}
                  {detail.active_workout_category && <div><span className="text-zinc-500 text-[10px] tracking-widest uppercase mr-2">Workout Focus</span>{detail.active_workout_category}</div>}
                </div>
              </div>

              <div className="border border-zinc-800 bg-[#141414] p-5 md:p-6" data-testid="access-control-card">
                <div className="flex items-center gap-2 mb-3">
                  <Lock size={14} className="text-[#E5B43A]" />
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#E5B43A]">Access Level</div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
                  {SCOPES.map((s) => (
                    <button
                      key={s.id} type="button"
                      onClick={() => changeScope(s.id)}
                      disabled={scopeBusy}
                      data-testid={`scope-set-${s.id}`}
                      className={`text-left px-4 py-3 border transition-colors disabled:opacity-50 ${detail.access_scope === s.id ? "border-[#E5B43A] bg-[#E5B43A]/15" : "border-zinc-800 hover:border-zinc-500"}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`w-3 h-3 rounded-full border ${detail.access_scope === s.id ? "bg-[#E5B43A] border-[#E5B43A]" : "border-zinc-600"}`} />
                        <span className="font-bold text-sm">{s.label}</span>
                      </div>
                      <div className="text-[11px] text-zinc-500 ml-5">{s.hint}</div>
                    </button>
                  ))}
                </div>
                <div className="border-t border-zinc-900 pt-4 flex items-center justify-between flex-wrap gap-3">
                  <div className="text-[11px] text-zinc-500 leading-snug">
                    Terminating deletes this client + ALL their data. Cannot be undone.
                  </div>
                  <button
                    onClick={() => setTerminateOpen(true)}
                    data-testid="oneonone-terminate-open"
                    className="inline-flex items-center gap-2 px-4 py-2.5 text-[10px] font-bold uppercase tracking-widest border border-red-500/50 text-red-400 hover:bg-red-500 hover:text-white transition-colors"
                  >
                    <ShieldAlert size={12} /> Terminate Membership
                  </button>
                </div>
              </div>

              <div className="border border-[#E5B43A] bg-[#141414] p-5 md:p-6" data-testid="session-tracker">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                  <div>
                    <div className="text-[10px] tracking-[0.3em] uppercase text-[#E5B43A] mb-1">Session Tracker</div>
                    <div className="font-display text-3xl">{detail.session_count} <span className="text-base text-zinc-400">of 30-day cycle</span></div>
                  </div>
                  <button onClick={redeemSession} disabled={redeeming} data-testid="redeem-session-btn" className="bg-[#E5B43A] text-black px-4 py-3 text-xs font-bold uppercase tracking-widest hover:bg-white hover:text-black transition-colors disabled:opacity-50">
                    {redeeming ? "Saving…" : <>+ Redeem Session</>}
                  </button>
                </div>
                {detail.sessions.length === 0 ? (
                  <p className="text-xs text-zinc-500 italic">No sessions recorded yet.</p>
                ) : (
                  <div className="border-t border-zinc-800 pt-3">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">Recent</div>
                    <ul className="space-y-1.5 text-sm">
                      {detail.sessions.slice(0, 8).map((s) => (
                        <li key={s.id} className="flex items-center justify-between gap-2" data-testid={`session-${s.id}`}>
                          <span className="text-zinc-300">{new Date(s.redeemed_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
                          <button onClick={() => unredeemSession(s.id)} className="text-zinc-600 hover:text-[#E5B43A] text-xs" title="Remove this session"><Trash2 size={14} /></button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="border border-zinc-800 bg-[#141414] p-5 md:p-6" data-testid="notes-section">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                  <div>
                    <div className="text-[10px] tracking-[0.3em] uppercase text-[#E5B43A] mb-1">Notes ({notes.length} / 12)</div>
                    <div className="font-display text-2xl">Coaching Log</div>
                  </div>
                  <div className="text-[10px] tracking-widest uppercase text-zinc-500">Newest first · oldest auto-evicts at 12</div>
                </div>

                {notes.length === 0 ? (
                  <p className="text-sm text-zinc-500 italic mb-4">No notes yet. Drop your first coaching insight below.</p>
                ) : (
                  <div className="border border-zinc-800 bg-[#0F0F0F] p-5 mb-4 min-h-[120px]">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-[10px] tracking-widest uppercase text-zinc-500">{noteIdx + 1} of {notes.length} · {note ? new Date(note.created_at).toLocaleDateString() : ""}</span>
                      <button onClick={() => note && deleteNote(note.id)} className="text-zinc-600 hover:text-[#E5B43A]" title="Delete this note"><Trash2 size={14} /></button>
                    </div>
                    <p className="text-base text-zinc-100 whitespace-pre-wrap break-words" data-testid="current-note-text">{note?.text || ""}</p>
                    <div className="mt-4 flex items-center justify-between gap-3">
                      <button onClick={() => setNoteIdx((i) => Math.max(0, i - 1))} disabled={noteIdx === 0} data-testid="note-prev" className="inline-flex items-center gap-1 text-xs font-bold tracking-widest uppercase text-zinc-400 hover:text-white disabled:opacity-30">
                        <ChevronLeft size={14} /> Newer
                      </button>
                      <button onClick={() => setNoteIdx((i) => Math.min(notes.length - 1, i + 1))} disabled={noteIdx === notes.length - 1} data-testid="note-next" className="inline-flex items-center gap-1 text-xs font-bold tracking-widest uppercase text-zinc-400 hover:text-white disabled:opacity-30">
                        Older <ChevronRight size={14} />
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-2">
                  <textarea
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="What did we work on? Form notes, struggles, wins…"
                    rows={2}
                    data-testid="new-note-input"
                    className="flex-1 bg-[#0A0A0A] border border-zinc-800 px-3 py-2 text-sm text-white focus:outline-none focus:border-[#E5B43A] resize-y"
                  />
                  <button onClick={addNote} disabled={savingNote || !noteText.trim()} data-testid="add-note-btn" className="bg-[#E5B43A] text-black px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-white hover:text-black transition-colors disabled:opacity-40 sm:self-stretch">
                    {savingNote ? "…" : <><Plus size={14} className="inline mr-1" />Note</>}
                  </button>
                </div>
              </div>

              <div className="border border-zinc-800 bg-[#141414] p-5 md:p-6" data-testid="progress-photos">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#E5B43A] mb-3">Progress Photos ({detail.check_ins.length})</div>
                {detail.check_ins.length === 0 ? (
                  <p className="text-sm text-zinc-500 italic">No check-ins recorded yet.</p>
                ) : (
                  <div className="space-y-4">
                    {detail.check_ins.slice(0, 6).map((ci) => (
                      <div key={ci.id} className="border border-zinc-900 p-3" data-testid={`checkin-${ci.id}`}>
                        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                          <div className="text-sm font-bold">{new Date(ci.created_at).toLocaleDateString()}</div>
                          <div className="text-xs text-zinc-500 flex gap-3 flex-wrap">
                            {ci.weight_lb && <span>{ci.weight_lb} lb</span>}
                            {ci.body_fat_pct && <span>{ci.body_fat_pct}% BF</span>}
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          {["photo_front", "photo_back", "photo_side"].map((slot) => (
                            ci[slot] ? <img key={slot} src={ci[slot]} alt={slot} className="w-full h-32 object-cover border border-zinc-800" /> :
                            <div key={slot} className="w-full h-32 border border-dashed border-zinc-800 bg-[#0A0A0A] grid place-items-center text-[10px] tracking-widest uppercase text-zinc-700"><Camera size={16} className="mb-1" />{slot.replace("photo_", "")}</div>
                          ))}
                        </div>
                        {ci.notes && <p className="mt-2 text-xs text-zinc-400 italic break-words">{ci.notes}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="border border-zinc-800 bg-[#141414] p-5 md:p-6" data-testid="client-workout-plan">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#E5B43A] mb-3">Current Workout Plan</div>
                {!detail.workout_plan ? (
                  <p className="text-sm text-zinc-500 italic">No workout plan generated yet.</p>
                ) : (
                  <div>
                    <div className="font-display text-lg mb-1">{(detail.workout_plan.category || "").replace(/_/g, " ")} · {detail.workout_plan.workouts?.length || 0} workouts</div>
                    <div className="text-xs text-zinc-500 mb-3">Created {new Date(detail.workout_plan.created_at).toLocaleDateString()}</div>
                    <ul className="space-y-1.5 text-sm text-zinc-300">
                      {(detail.workout_plan.workouts || []).map((w) => (
                        <li key={w.name} className="flex gap-2"><span className="text-[#E5B43A] shrink-0">·</span><span className="break-words min-w-0">{w.name}{w.focus ? ` — ${w.focus}` : ""}</span></li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="border border-zinc-800 bg-[#141414] p-5 md:p-6" data-testid="client-meal-plan">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#E5B43A] mb-3">Current Meal Plan</div>
                {!detail.meal_plan ? (
                  <p className="text-sm text-zinc-500 italic">No meal plan generated yet.</p>
                ) : (
                  <div>
                    <div className="font-display text-lg mb-1">{detail.meal_plan.goal?.replace("_", " ")} · {detail.meal_plan.meals?.length || 0} meals</div>
                    <div className="text-xs text-zinc-500 mb-3">Created {new Date(detail.meal_plan.created_at).toLocaleDateString()}</div>
                    <ul className="space-y-1.5 text-sm text-zinc-300">
                      {(detail.meal_plan.meals || []).map((m) => (
                        <li key={`${m.slot || ""}-${m.name}`} className="flex gap-2"><span className="text-[#E5B43A] shrink-0">·</span><span className="break-words min-w-0">{m.slot ? `${m.slot}: ` : ""}{m.name}</span></li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </section>
      </div>

      {terminateOpen && detail && (
        <div
          className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          data-testid="oneonone-terminate-modal"
          onClick={(e) => { if (e.target === e.currentTarget && !terminateBusy) setTerminateOpen(false); }}
        >
          <div className="w-full sm:max-w-lg border border-red-500 bg-[#0F0F0F] shadow-2xl">
            <div className="px-6 py-4 border-b border-red-500/50 bg-red-500/10 flex items-center gap-3">
              <ShieldAlert className="text-red-400 shrink-0" size={18} />
              <div className="text-[10px] tracking-[0.3em] uppercase text-red-400 font-bold">Terminate · Irreversible</div>
            </div>
            <div className="px-6 py-6 space-y-4">
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-1">Client</div>
                <div className="font-display text-xl md:text-2xl leading-tight break-all">{detail.name}</div>
                <div className="text-zinc-400 text-sm break-all">{detail.email}</div>
              </div>
              <div className="border border-zinc-800 bg-[#141414] px-4 py-3">
                <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">This will permanently delete</div>
                <ul className="space-y-1.5 text-sm text-zinc-300">
                  <li className="flex gap-2"><span className="text-red-400 shrink-0">×</span><span>Their account &amp; login</span></li>
                  <li className="flex gap-2"><span className="text-red-400 shrink-0">×</span><span>All meal plans, workouts, sessions &amp; notes</span></li>
                  <li className="flex gap-2"><span className="text-red-400 shrink-0">×</span><span>All progress photos &amp; check-ins</span></li>
                  <li className="flex gap-2"><span className="text-red-400 shrink-0">×</span><span>Any pending 1-on-1 requests</span></li>
                </ul>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-zinc-800 bg-[#0A0A0A] flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <button onClick={() => setTerminateOpen(false)} disabled={terminateBusy} data-testid="oneonone-terminate-cancel" className="px-5 py-3 text-xs font-bold uppercase tracking-widest border border-zinc-700 text-zinc-300 hover:border-white hover:text-white transition-colors disabled:opacity-50">Cancel</button>
              <button onClick={terminateClient} disabled={terminateBusy} data-testid="oneonone-terminate-confirm" className="px-5 py-3 text-xs font-bold uppercase tracking-widest bg-red-500 text-white hover:bg-red-600 transition-colors disabled:opacity-60">
                {terminateBusy ? "Terminating..." : "Terminate Permanently"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
